import QRCode from 'qrcode'

/** ID card QR (`card:<token>`), resolved by the backend so a reset token invalidates old cards. */
export const isCardScan = (value: string) => /^card:\S+/i.test(value.trim())

/** QR from the first card version, which carried the bare worker code and can't be revoked. */
export const isLegacyWorkerQr = (value: string) => /^worker:/i.test(value.trim())

/**
 * Worker code from a scanned or typed value: a plain code, `worker:<code>` (ID card QR),
 * or a URL carrying `?worker_code=` / `?code=`.
 */
export function parseWorkerScan(value: string) {
  const text = value.trim()
  if (!text) return ''
  const prefixed = /^(?:worker|code|qr):(.*)$/i.exec(text)
  if (prefixed) return prefixed[1].trim()
  if (/^https?:\/\//i.test(text)) {
    try {
      const url = new URL(text)
      const fromQuery = url.searchParams.get('worker_code') || url.searchParams.get('code') || url.searchParams.get('qr')
      if (fromQuery?.trim()) return fromQuery.trim()
      return decodeURIComponent(url.pathname.replace(/\/+$/, '').split('/').pop() || '').trim()
    } catch {
      return text
    }
  }
  return text
}

export type WorkerIdCardDetails = {
  name: string
  workerCode: string
  qrPayload: string
  role?: string | null
  orgName?: string | null
  photoUrl?: string | null
  logoUrl?: string | null
}

// CR80 card, portrait, at 300 dpi.
const CARD_WIDTH = 638
const CARD_HEIGHT = 1012
const BRAND = '#1d4ed8'

const loadImage = (src?: string | null) => new Promise<HTMLImageElement | null>((resolve) => {
  if (!src) {
    resolve(null)
    return
  }
  const image = new Image()
  // Without CORS the image would taint the canvas and block the download, so it's skipped instead.
  image.crossOrigin = 'anonymous'
  const timer = window.setTimeout(() => resolve(null), 5000)
  image.onload = () => {
    window.clearTimeout(timer)
    resolve(image)
  }
  image.onerror = () => {
    window.clearTimeout(timer)
    resolve(null)
  }
  image.src = src
})

const roundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) => {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + width, y, x + width, y + height, radius)
  ctx.arcTo(x + width, y + height, x, y + height, radius)
  ctx.arcTo(x, y + height, x, y, radius)
  ctx.arcTo(x, y, x + width, y, radius)
  ctx.closePath()
}

const fitText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number, weight: string, maxSize: number, minSize = 18) => {
  let size = maxSize
  ctx.font = `${weight} ${size}px Inter, "Segoe UI", Arial, sans-serif`
  while (size > minSize && ctx.measureText(text).width > maxWidth) {
    size -= 2
    ctx.font = `${weight} ${size}px Inter, "Segoe UI", Arial, sans-serif`
  }
  if (ctx.measureText(text).width <= maxWidth) return text
  let trimmed = text
  while (trimmed.length > 1 && ctx.measureText(`${trimmed}…`).width > maxWidth) trimmed = trimmed.slice(0, -1)
  return `${trimmed}…`
}

const initialsOf = (name: string) => name
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0]?.toUpperCase() ?? '')
  .join('') || '?'

export async function renderWorkerIdCard(details: WorkerIdCardDetails): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas')
  canvas.width = CARD_WIDTH
  canvas.height = CARD_HEIGHT
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas is not supported in this browser.')

  const [photo, logo, qrDataUrl] = await Promise.all([
    loadImage(details.photoUrl),
    loadImage(details.logoUrl),
    QRCode.toDataURL(details.qrPayload, { errorCorrectionLevel: 'M', margin: 1, width: 340 }),
  ])
  const qr = await loadImage(qrDataUrl)

  const center = CARD_WIDTH / 2
  const textWidth = CARD_WIDTH - 80

  roundedRect(ctx, 0, 0, CARD_WIDTH, CARD_HEIGHT, 36)
  ctx.save()
  ctx.clip()
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT)

  ctx.fillStyle = BRAND
  ctx.fillRect(0, 0, CARD_WIDTH, 190)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillStyle = '#ffffff'
  if (logo) {
    const logoSize = 72
    const ratio = logo.width / logo.height || 1
    const width = Math.min(logoSize * ratio, 220)
    ctx.drawImage(logo, center - width / 2, 28, width, logoSize)
    ctx.fillText(fitText(ctx, details.orgName || 'Employee ID', textWidth, '700', 34), center, 140)
  } else {
    ctx.fillText(fitText(ctx, details.orgName || 'Employee ID', textWidth, '700', 40), center, 95)
  }

  const photoY = 300
  const photoRadius = 110
  ctx.fillStyle = '#ffffff'
  ctx.beginPath()
  ctx.arc(center, photoY, photoRadius + 8, 0, Math.PI * 2)
  ctx.fill()
  ctx.save()
  ctx.beginPath()
  ctx.arc(center, photoY, photoRadius, 0, Math.PI * 2)
  ctx.clip()
  if (photo) {
    const side = Math.min(photo.width, photo.height)
    ctx.drawImage(
      photo,
      (photo.width - side) / 2,
      (photo.height - side) / 2,
      side,
      side,
      center - photoRadius,
      photoY - photoRadius,
      photoRadius * 2,
      photoRadius * 2,
    )
  } else {
    ctx.fillStyle = '#dbeafe'
    ctx.fillRect(center - photoRadius, photoY - photoRadius, photoRadius * 2, photoRadius * 2)
    ctx.fillStyle = BRAND
    ctx.font = '700 80px Inter, "Segoe UI", Arial, sans-serif'
    ctx.fillText(initialsOf(details.name), center, photoY + 4)
  }
  ctx.restore()

  ctx.fillStyle = '#0f172a'
  ctx.fillText(fitText(ctx, details.name, textWidth, '700', 46), center, 465)
  if (details.role) {
    ctx.fillStyle = '#475569'
    ctx.fillText(fitText(ctx, details.role, textWidth, '500', 28), center, 515)
  }
  ctx.fillStyle = '#0f172a'
  ctx.fillText(fitText(ctx, `ID ${details.workerCode}`, textWidth, '600', 30), center, 565)

  if (qr) {
    const qrSize = 340
    ctx.drawImage(qr, center - qrSize / 2, 620, qrSize, qrSize)
  }
  ctx.restore()

  return canvas
}

export async function downloadWorkerIdCard(details: WorkerIdCardDetails) {
  const canvas = await renderWorkerIdCard(details)
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
  if (!blob) throw new Error('Could not create the ID card image.')
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  const safeName = details.name.replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '') || 'worker'
  link.href = url
  link.download = `id-card-${safeName}-${details.workerCode}.png`
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}
